// src/redux/slices/drawers/controlled_substance_logging/containerLogDrawerSlice.ts
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import { csl, IAppointmentLite, patients } from "lobbie";
import { ContainerStatusFlow } from "src/classes/ContainerStatusFlow";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import type { RootState } from "src/redux/store";
import {
    ECSLCheckoutStatus,
    ECSLContainerLogBatchType,
    ECSLContainerLogStatus,
    ECSLContainerLogTab,
    ECSLContainerStatus,
    ECSLLogOptionType,
    ECSLLogType,
    ECSLSignatureRequestType,
    ESearchTableType,
} from "src/types/global_enums";
import { createDrawerActionMeta, createDrawerThunkMeta } from "../stackableDrawerConstant";

import { generateUUID, handleError, logDev, notify } from "src/utils";
import { match, P } from "ts-pattern";
import { triggerSearchTableRefetch } from "../../searchTableRefetchSlice";
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";
import {
    createSignaturePayloads,
    createStartSignatureRequestThunk,
    createSubmitSignatureRequestThunk,
    initialNullificationCapableState,
    initialWitnessSignatureState,
    INullificationCapableSlice,
    IWitnessSignatureState,
    processSignatureData,
    submitBatchSignatures,
} from "./signatureRequestService";

export interface IContainerLogDrawerState
    extends IBaseDrawerState,
        IWitnessSignatureState,
        INullificationCapableSlice {
    // Drawer state
    show: boolean;
    activeTab: ECSLContainerLogTab;
    patientId?: number;
    appointmentId?: number;
    onLogEntrySave?: () => void;

    // Details state
    containerLogId?: number;
    isEditMode: boolean;
    logDetails?: csl.IContainerLogFullDetails;

    isLoading: boolean;
    isSubmitting: boolean; // used to tracking submitting for all kinds of actions, container log creation, updates, nullifications, etc
    error: string | null;

    // New state for log entry
    selectedPatient?: patients.IAccountPatientSearch;
    selectedAppointment?: IAppointmentLite;
    selectedMedication?: csl.INdcVariantFullDetails;
    hideAdministeringCard: boolean;
    selectedContainer?: csl.IContainerFullDetails;
    occurredOn?: string;
    amountRemoved?: number;
    isAdministered?: boolean; // vs dispensed
    primaryLogOption?: csl.ILogEntryOption; // used for both administering/dispensing as well as disposal log creation
    secondaryLogOption?: csl.ILogEntryOption; // used for both administering/dispensing as well as disposal log creation
    comments?: string;

    // Add checkout related state
    checkoutStatus?: ECSLCheckoutStatus;
    checkoutMessage?: string;
    isSubmittingCheckOut: boolean;
    checkoutError: string | null;

    showWastingCard: boolean;
    wastingAmount?: number;
    wastingMethodOption?: csl.ILogEntryOption;
    wastingReasonOption?: csl.ILogEntryOption;
    wastingComments?: string;

    // checking out a specific container state
    showExistingContainerModal: boolean;
    requestedContainerId?: number;
    existingContainerCheckout?: csl.IContainerCheckout;

    // manual overriding of automatic container selection
    showContainerSelectModal: boolean;

    // container status change state
    isStatusChangeMode: boolean;
    targetContainerStatus?: ECSLContainerStatus;
    containerStatusComments?: string;

    // used during log entry for administering/dispensing/wasting
    setContainerAsEmpty: boolean;

    // batch details
    batchId?: number;
    batchType?: ECSLContainerLogBatchType;
    containerLogs?: csl.IContainerLogFullDetails[];
}

export const initialStateContainerLogDrawer: IContainerLogDrawerState = {
    ...initialWitnessSignatureState,
    drawerHistoryInstanceId: "containerLogDrawer",
    show: false,
    activeTab: ECSLContainerLogTab.USAGE,
    isLoading: false,
    error: null,

    containerLogId: undefined,
    isEditMode: false,

    // new log entry state
    selectedPatient: undefined,
    selectedAppointment: undefined,
    selectedMedication: undefined,
    selectedContainer: undefined,
    hideAdministeringCard: false,
    occurredOn: undefined,
    amountRemoved: undefined,
    isAdministered: undefined,
    primaryLogOption: undefined,
    secondaryLogOption: undefined,
    comments: "",

    // checkout state
    checkoutStatus: undefined,
    checkoutMessage: undefined,
    isSubmittingCheckOut: false,
    checkoutError: null,

    isSubmitting: false,

    showWastingCard: false,
    wastingAmount: undefined,
    wastingMethodOption: undefined,
    wastingReasonOption: undefined,
    wastingComments: "",

    showExistingContainerModal: false,
    requestedContainerId: undefined,
    existingContainerCheckout: undefined,

    ...initialNullificationCapableState,

    showContainerSelectModal: false,

    isStatusChangeMode: false,
    targetContainerStatus: undefined,
    containerStatusComments: "",
    setContainerAsEmpty: false,

    // Add new batch editing related fields
    batchId: undefined,
    batchType: undefined,
    containerLogs: undefined,
};

const validateContainerNotPending = (container: csl.IContainerFullDetails) => {
    if (container.status === ECSLContainerStatus.PendingSignature) {
        return "Container is pending and cannot be selected.";
    }
    return null;
};

export const openContainerLogDrawer = createAsyncThunk<
    csl.IContainerLogFullDetails,
    { containerLogId: number },
    { rejectValue: string }
>(
    "containerLogDrawer/openContainerLogDrawer",
    async (params, { rejectWithValue, dispatch }) => {
        if (params.containerLogId) {
            const containerLogDetails = await dispatch(
                fetchContainerLogDetails(params.containerLogId),
            ).unwrap();
            return containerLogDetails;
        }
        return rejectWithValue("No containerLogId provided");
    },
    createDrawerThunkMeta("show"),
);

const checkoutContainerBase = async (
    containerId: number,
    checkInExisting: boolean = false,
): Promise<{
    data: csl.IContainerCheckout | null;
    success: boolean;
    error?: { status: ECSLCheckoutStatus; checkout: csl.IContainerCheckout | null };
}> => {
    try {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/checkout-specific`;
        const { data } = await axios.post<csl.IContainerCheckout>(
            route,
            {
                containerId,
                checkInExisting,
            },
            { withCredentials: true },
        );
        // If force takeover was successful, show a success notification
        if (data.checkoutStatus === ECSLCheckoutStatus.TAKEOVER_FROM_OTHER) {
            notify({
                level: "success",
                title: "Container Transferred",
                message: "Successfully transferred container",
                duration: 5000,
            });
        }
        // Handle checkout status scenarios
        if (
            data.checkoutStatus === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF &&
            data.id !== containerId
        ) {
            return {
                data,
                success: false,
                error: {
                    status: ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF,
                    checkout: data,
                },
            };
        }
        if (data.checkoutStatus === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_OTHER) {
            return {
                data,
                success: false,
                error: {
                    status: ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_OTHER,
                    checkout: data,
                },
            };
        }
        if (data.checkoutStatus === ECSLCheckoutStatus.NO_TAKEOVER_ALLOWED) {
            return {
                data,
                success: false,
                error: {
                    status: ECSLCheckoutStatus.NO_TAKEOVER_ALLOWED,
                    checkout: data,
                },
            };
        }
        return { data, success: true };
    } catch (error: any) {
        notify({ level: "error", title: "Error", message: "Failed to checkout container" });
        return {
            data: null,
            success: false,
            error: {
                status: ECSLCheckoutStatus.ERROR,
                checkout: null,
            },
        };
    }
};

/*
 * Given the container log drawer is not open, will attempt to checkout the specified container to start a container log
 */
export const checkoutSpecificContainer = createAsyncThunk<
    csl.IContainerCheckout,
    { containerId: number; checkInExisting?: boolean },
    { rejectValue: { status: ECSLCheckoutStatus; checkout: csl.IContainerCheckout | null } }
>(
    "containerLogDrawer/checkoutSpecificContainer",
    async (params, { rejectWithValue }) => {
        const result = await checkoutContainerBase(
            params.containerId,
            params.checkInExisting || false,
        );
        if (!result.success) {
            return rejectWithValue(result.error!);
        }
        if (!result.data) {
            return rejectWithValue({
                status: ECSLCheckoutStatus.ERROR,
                checkout: null,
            });
        }
        return result.data;
    },
    createDrawerThunkMeta("show"),
);

/*
 * Given the container log drawer is open, this action will attempt to checkout a different container
 */
export const overrideSelectedContainer = createAsyncThunk<
    csl.IContainerCheckout | null,
    { containerId: number; checkInExisting?: boolean },
    { rejectValue: { status: ECSLCheckoutStatus; checkout: csl.IContainerCheckout | null } }
>("containerLogDrawer/overrideSelectedContainer", async (params, { rejectWithValue }) => {
    const containerDetailsResponse = await axios.get<csl.IContainerFullDetails>(
        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/${params.containerId}`,
        { withCredentials: true },
    );
    const containerDetails = containerDetailsResponse.data;
    if (containerDetails.status === ECSLContainerStatus.PendingSignature) {
        return rejectWithValue({ status: ECSLCheckoutStatus.CONTAINER_PENDING, checkout: null });
    }

    // First attempt with provided checkInExisting value
    let result = await checkoutContainerBase(params.containerId, params.checkInExisting || false);
    // If it fails because user already has a container checked out to themselves,
    // automatically retry with checkInExisting=true
    if (
        !result.success &&
        result.error?.status === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF &&
        !params.checkInExisting
    ) {
        // Second attempt with forced check-in
        result = await checkoutContainerBase(params.containerId, true);
    }
    if (!result.success) {
        return rejectWithValue(result.error!);
    }
    return result.data;
});

export const switchContainerForStatusChange = createAsyncThunk<
    csl.IContainerFullDetails,
    { containerId: number },
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/selectContainerForStatusChange",
    async (params, { dispatch, rejectWithValue }) => {
        try {
            const containerDetails = await dispatch(
                fetchContainerDetails(params.containerId),
            ).unwrap();
            logDev("NNN selectContainerForStatusChange containerDetails", containerDetails);
            if (!containerDetails) {
                return rejectWithValue("Container not found");
            }
            const pendingError = validateContainerNotPending(containerDetails);
            if (pendingError) {
                notify({ level: "error", title: "Error", message: pendingError });
                return rejectWithValue(pendingError);
            }
            return containerDetails;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to fetch container details");
        }
    },
);

export const changeContainerStatus = createAsyncThunk<
    void,
    { containerId: number; newStatus: ECSLContainerStatus; comments?: string },
    { state: RootState; rejectValue: string }
>(
    // this purposely isn't connected to the drawer middleware open actions because
    // it might submit immediately or might call createNewLogEntry which will run the open drawer logic
    "containerLogDrawer/changeContainerStatus",
    async (params, { dispatch, rejectWithValue }) => {
        try {
            const containerInfo = await dispatch(
                fetchContainerDetails(params.containerId),
            ).unwrap();

            if (!containerInfo) {
                return rejectWithValue("Container not found");
            }
            const statusFlow = new ContainerStatusFlow(containerInfo.status as ECSLContainerStatus);
            if (!statusFlow.canTransitionTo(params.newStatus)) {
                return rejectWithValue(
                    `Cannot transition from ${containerInfo.status} to ${params.newStatus}`,
                );
            }
            // Set up the drawer state for status change in one dispatch
            dispatch(
                createNewLogEntry({
                    isStatusChange: true,
                    targetStatus: params.newStatus,
                    container: containerInfo,
                    comments: params.comments || "",
                }),
            );
            // Check if this transition requires a signature
            const requiresSignature = statusFlow.requiresSignatureForTransition(params.newStatus);
            if (requiresSignature) {
                // For transitions requiring signature, open the drawer with pre-filled data
                return; // User will complete signature and submit manually
            } else {
                // For transitions not requiring signature, submit immediately
                await dispatch(createLogEntries()).unwrap();
                dispatch(
                    triggerSearchTableRefetch([
                        ESearchTableType.CSL_CURRENT_INVENTORY,
                        ESearchTableType.CSL_CONTAINER_LOGS,
                    ]),
                );
            }
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to change container status");
        }
    },
);

export const fetchContainerDetails = createAsyncThunk<
    csl.IContainerFullDetails,
    number,
    { rejectValue: string }
>("containerLogDrawer/fetchContainerDetails", async (containerId, { rejectWithValue }) => {
    try {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/${containerId}`;
        const { data } = await axios.get<csl.IContainerFullDetails>(route, {
            withCredentials: true,
        });
        return data;
    } catch (error: any) {
        return rejectWithValue("Failed to fetch container details");
    }
});

export const fetchContainerDetailsAndSet = createAsyncThunk<
    csl.IContainerFullDetails,
    number,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/fetchContainerDetailsAndSet",
    async (containerId, { dispatch, rejectWithValue }) => {
        try {
            const containerDetails = await dispatch(fetchContainerDetails(containerId)).unwrap();
            if (!containerDetails) {
                return rejectWithValue("Container not found");
            }
            return containerDetails;
        } catch (error: any) {
            return rejectWithValue("Failed to fetch container details");
        }
    },
);

export const fetchContainerLogDetails = createAsyncThunk<
    csl.IContainerLogFullDetails,
    number,
    { rejectValue: string }
>("containerLogDrawer/fetchContainerLogDetails", async (containerLogId, { rejectWithValue }) => {
    try {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/${containerLogId}/details`;
        const { data } = await axios.get<csl.IContainerLogFullDetails>(route, {
            withCredentials: true,
        });
        return data;
    } catch (error: any) {
        return rejectWithValue("Failed to fetch container log details");
    }
});

export const initializeLogEntryWithAppointment = createAsyncThunk<
    { appointment: IAppointmentLite; patient: patients.IAccountPatientSearch },
    number,
    { rejectValue: string }
>(
    "containerLogDrawer/initializeLogEntryWithAppointment",
    async (appointmentId: number, { rejectWithValue }) => {
        try {
            // First fetch the appointment details
            const appointmentRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/search/${appointmentId}`;
            const appointmentResponse = await axios.get<IAppointmentLite>(appointmentRoute, {
                withCredentials: true,
            });
            const appointment = appointmentResponse.data;
            // Then fetch the patient details
            const patientRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/patients/search_v2/${appointment.accountPatientId}`;
            const patientResponse = await axios.get<patients.IAccountPatientSearch>(patientRoute, {
                withCredentials: true,
            });
            return { appointment: appointment, patient: patientResponse.data };
        } catch (error: any) {
            notify({
                level: "error",
                title: "Error",
                message:
                    "Failed to load appointment and patient details when opening up the container log entry drawer",
            });
            return rejectWithValue("Failed to load appointment and patient details");
        }
    },
    createDrawerThunkMeta("show"),
);

export const startContainerLogSignature = createStartSignatureRequestThunk(
    "containerLogDrawer/startContainerLogSignature",
);

export const startContainerLogNullificationSigning = createStartSignatureRequestThunk(
    "containerLogDrawer/startContainerLogNullificationSigning",
    true,
);

export const createLogOption = createAsyncThunk<
    csl.ILogEntryOption,
    { name: string; enumType: ECSLLogOptionType },
    { rejectValue: string }
>("containerLogDrawer/createLogOption", async (params, { rejectWithValue }) => {
    try {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log-option`;
        const { data } = await axios.post<csl.ILogEntryOption>(
            route,
            { name: params.name, enumType: params.enumType },
            { withCredentials: true },
        );
        return data;
    } catch (error: any) {
        notify({ level: "error", title: "Error", message: "Failed to create log option" });
        return rejectWithValue("Failed to create log option");
    }
});

export const checkoutContainer = createAsyncThunk<
    csl.IContainerCheckout,
    { containerId: number },
    { rejectValue: string }
>("containerLogDrawer/checkoutContainer", async (params, { rejectWithValue }) => {
    try {
        const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/${params.containerId}/checkout`;
        const { data } = await axios.post<csl.IContainerCheckout>(
            route,
            {},
            { withCredentials: true },
        );
        return data;
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue("Failed to checkout container");
    }
});

export const createLogEntries = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/createLogEntries",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState().containerLogDrawer;
        if (!state.selectedContainer || !state.occurredOn) {
            return rejectWithValue("Missing required fields");
        }
        try {
            const occurredOnISO = state.occurredOn;
            const containerLogs: csl.ICreateLogEntry[] = [];
            // Handle status change log creation
            if (state.isStatusChangeMode && state.targetContainerStatus) {
                containerLogs.push({
                    containerId: state.selectedContainer.id,
                    logType: ECSLLogType.STATUS,
                    occurredOnISO,
                    comments: state.containerStatusComments || "",
                    newContainerStatus: state.targetContainerStatus,
                });
            } else if (state.activeTab === ECSLContainerLogTab.DISPOSAL) {
                if (!state.primaryLogOption || !state.secondaryLogOption || !state.witnessSignee) {
                    return rejectWithValue("Missing required fields for disposal");
                }
                containerLogs.push({
                    containerId: state.selectedContainer.id,
                    logType: ECSLLogType.DISPOSING,
                    primaryLogOptionId: state.primaryLogOption.id,
                    secondaryLogOptionId: state.secondaryLogOption.id,
                    comments: state.comments || "",
                    occurredOnISO,
                });
            }
            // Handle regular log entries (administering/dispensing/wasting)
            else {
                // Only add administering log if not hidden
                if (!state.hideAdministeringCard) {
                    if (!state.amountRemoved || !state.primarySignee || !state.selectedPatient) {
                        return rejectWithValue("Missing required fields for administration");
                    }

                    containerLogs.push({
                        containerId: state.selectedContainer.id,
                        amount: state.amountRemoved,
                        logType: state.isAdministered
                            ? ECSLLogType.ADMINISTERING
                            : ECSLLogType.DISPENSING,
                        primaryLogOptionId: state.primaryLogOption?.id,
                        secondaryLogOptionId: state.secondaryLogOption?.id,
                        accountPatientId: state.selectedPatient?.id,
                        appointmentId: state.selectedAppointment?.id,
                        comments: state.comments,
                        occurredOnISO,
                    });
                }
                // Add wasting log if shown
                if (state.showWastingCard) {
                    if (
                        !state.wastingAmount ||
                        !state.wastingReasonOption ||
                        !state.wastingMethodOption
                    ) {
                        return rejectWithValue("Missing required fields for wasting");
                    }
                    containerLogs.push({
                        containerId: state.selectedContainer.id,
                        amount: state.wastingAmount!,
                        logType: ECSLLogType.WASTING,
                        primaryLogOptionId: state.wastingReasonOption!.id,
                        secondaryLogOptionId: state.wastingMethodOption!.id,
                        accountPatientId: state.selectedPatient?.id,
                        appointmentId: state.selectedAppointment?.id,
                        comments: state.wastingComments,
                        occurredOnISO,
                    });
                }
                // Add status change log if setContainerAsEmpty is true
                if (state.setContainerAsEmpty) {
                    containerLogs.push({
                        containerId: state.selectedContainer.id,
                        logType: ECSLLogType.STATUS,
                        occurredOnISO, // Same timestamp as the usage log
                        comments: "Container set to empty during log entry",
                        newContainerStatus: ECSLContainerStatus.EMPTY,
                    });
                }
            }
            if (containerLogs.length === 0) {
                return rejectWithValue("No valid log entries to create");
            }
            //
            if (
                !state.primarySignee?.id &&
                containerLogs.some((log) => (log.logType as ECSLLogType) !== ECSLLogType.STATUS)
            ) {
                return rejectWithValue("Missing primary signee");
            }
            const batchPayload: csl.IBatchCreateLogEntry = {
                containerLogs,
                primarySignerId: state.primarySignee?.id,
                witnessSignerId: state.witnessSignee?.id,
            };
            const createLogRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/batch`;
            const response = await axios.post<csl.IContainerLogFullDetails[]>(
                createLogRoute,
                batchPayload,
                { withCredentials: true },
            );
            const allSignatureRequests = response.data.flatMap((log) => log.signatureRequests);
            const signaturePayloads = createSignaturePayloads(allSignatureRequests, state);
            const message = match({
                disposal: state.activeTab === ECSLContainerLogTab.DISPOSAL,
                statusChange: state.isStatusChangeMode,
            })
                .with({ disposal: true }, () => "Disposal log entry created successfully")
                .with({ statusChange: true }, () => `Container status changed successfully`)
                .otherwise(() => "Log entries created successfully");
            await submitBatchSignatures(signaturePayloads, {
                successMessage: message,
                showSuccessNotification: true,
            });
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                    ESearchTableType.CSL_CONTAINER_ARCHIVE, // can create status logs that could add or remove containers from archive
                ]),
            );
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to create log entries");
        }
    },
    createDrawerThunkMeta("hide"),
);

export const enterBatchEditMode = createAsyncThunk<
    csl.IContainerLogBatchFullDetails,
    number, // containerLogId
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/enterBatchEditMode",
    async (containerLogId, { rejectWithValue }) => {
        try {
            // Use the new endpoint with STANDARD batch type (0)
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/${containerLogId}/batch?batchType=${ECSLContainerLogBatchType.STANDARD}`;
            const { data } = await axios.get<csl.IContainerLogBatchFullDetails>(route, {
                withCredentials: true,
            });
            return data;
        } catch (error: any) {
            return rejectWithValue("Failed to fetch related container logs");
        }
    },
    createDrawerThunkMeta("show"),
);

export const updateLogEntries = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/updateLogEntries",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState().containerLogDrawer;
        try {
            const occurredOnISO = state.occurredOn;
            // Prepare payload collections
            const updates: any[] = [];
            const creates: any[] = [];
            const cancels: number[] = [];
            // Gather operations based on UI state
            determineContainerLogUpdates(state, updates, creates, cancels, occurredOnISO);
            // Process operations in sequence: cancels -> updates -> creates
            const results = {
                signatureRequests: [] as csl.ISignatureRequest[],
            };
            // 1. First handle cancellations
            if (cancels.length > 0) {
                await processCancellations(cancels);
            }
            // 2. Then handle updates
            if (updates.length > 0) {
                const updateResponse = await processUpdates(updates, state);
                if (updateResponse) {
                    results.signatureRequests = [
                        ...results.signatureRequests,
                        ...updateResponse.flatMap((update) => update.signatureRequests),
                    ];
                }
            }
            // 3. Finally handle creates - use the batch ID from state if available
            if (creates.length > 0) {
                const createResponse = await processCreates(
                    creates,
                    state.primarySignee?.id,
                    state.witnessSignee?.id,
                    state.batchId, // Pass the batch ID from state
                );
                if (createResponse) {
                    results.signatureRequests = [
                        ...results.signatureRequests,
                        ...createResponse.signatureRequests,
                    ];
                }
            }
            // Process all signature requests
            if (results.signatureRequests.length > 0) {
                const signaturePayloads = createSignaturePayloads(results.signatureRequests, state);
                await submitBatchSignatures(signaturePayloads, {
                    successMessage: "Log entries updated successfully",
                    showSuccessNotification: true,
                });
            } else {
                // If no signatures but operations were successful, still show success
                notify({
                    level: "success",
                    title: "Success",
                    message: "Log entries updated successfully",
                });
            }
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
            return;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to update log entries");
        }
    },
    createDrawerThunkMeta("hide"),
);

function determineContainerLogUpdates(
    state: IContainerLogDrawerState,
    updates: any[],
    creates: any[],
    cancels: number[],
    occurredOnISO: string | undefined,
) {
    // Find logs by type from containerLogs if available
    const adminLog = state.containerLogs?.find(
        (log) =>
            log.logType === ECSLLogType.ADMINISTERING || log.logType === ECSLLogType.DISPENSING,
    );
    const wastingLog = state.containerLogs?.find((log) => log.logType === ECSLLogType.WASTING);
    const statusLog = state.containerLogs?.find((log) => log.logType === ECSLLogType.STATUS);
    // Handle admin log (administering/dispensing)
    if (!state.hideAdministeringCard) {
        const logType = state.isAdministered ? ECSLLogType.ADMINISTERING : ECSLLogType.DISPENSING;

        if (adminLog) {
            // Check if any property has changed before updating
            const hasChanged =
                adminLog.logType !== logType ||
                Math.abs(adminLog.amount || 0) !== (state.amountRemoved || 0) ||
                adminLog.primaryLogOption?.id !== state.primaryLogOption?.id ||
                adminLog.secondaryLogOption?.id !== state.secondaryLogOption?.id ||
                adminLog.accountPatient?.id !== state.selectedPatient?.id ||
                (adminLog.appointment?.id !== state.selectedAppointment?.id &&
                    (adminLog.appointment?.id || state.selectedAppointment?.id)) || // Handle null/undefined
                adminLog.comments !== state.comments ||
                adminLog.occurredOnISO !== occurredOnISO;

            if (hasChanged) {
                updates.push({
                    containerLogId: adminLog.id,
                    amount: state.amountRemoved,
                    logType,
                    primaryLogOptionId: state.primaryLogOption?.id,
                    secondaryLogOptionId: state.secondaryLogOption?.id,
                    accountPatientId: state.selectedPatient?.id,
                    appointmentId: state.selectedAppointment?.id,
                    comments: state.comments,
                    occurredOnISO,
                });
            }
        } else {
            // Create new admin log
            creates.push({
                containerId: state.selectedContainer?.id,
                amount: state.amountRemoved,
                logType,
                primaryLogOptionId: state.primaryLogOption?.id,
                secondaryLogOptionId: state.secondaryLogOption?.id,
                accountPatientId: state.selectedPatient?.id,
                appointmentId: state.selectedAppointment?.id,
                comments: state.comments,
                occurredOnISO,
            });
        }
    } else if (adminLog) {
        // Cancel admin log if hidden
        cancels.push(adminLog.id);
    }
    // Handle wasting log
    if (state.showWastingCard) {
        if (wastingLog) {
            // Check if any property has changed before updating
            const hasChanged =
                Math.abs(wastingLog.amount || 0) !== (state.wastingAmount || 0) ||
                wastingLog.primaryLogOption?.id !== state.wastingReasonOption?.id ||
                wastingLog.secondaryLogOption?.id !== state.wastingMethodOption?.id ||
                wastingLog.accountPatient?.id !== state.selectedPatient?.id ||
                (wastingLog.appointment?.id !== state.selectedAppointment?.id &&
                    (wastingLog.appointment?.id || state.selectedAppointment?.id)) || // Handle null/undefined
                wastingLog.comments !== state.wastingComments ||
                wastingLog.occurredOnISO !== occurredOnISO;

            if (hasChanged) {
                updates.push({
                    containerLogId: wastingLog.id,
                    amount: state.wastingAmount,
                    logType: ECSLLogType.WASTING,
                    primaryLogOptionId: state.wastingReasonOption?.id,
                    secondaryLogOptionId: state.wastingMethodOption?.id,
                    accountPatientId: state.selectedPatient?.id,
                    appointmentId: state.selectedAppointment?.id,
                    comments: state.wastingComments,
                    occurredOnISO,
                });
            }
        } else {
            // Create new wasting log
            creates.push({
                containerId: state.selectedContainer?.id,
                amount: state.wastingAmount,
                logType: ECSLLogType.WASTING,
                primaryLogOptionId: state.wastingReasonOption?.id,
                secondaryLogOptionId: state.wastingMethodOption?.id,
                accountPatientId: state.selectedPatient?.id,
                appointmentId: state.selectedAppointment?.id,
                comments: state.wastingComments,
                occurredOnISO,
            });
        }
    } else if (wastingLog) {
        // Cancel wasting log if hidden
        cancels.push(wastingLog.id);
    }
    // Handle status log (set container as empty)
    if (state.setContainerAsEmpty) {
        if (statusLog) {
            const hasChanged =
                statusLog.newContainerStatus !== ECSLContainerStatus.EMPTY ||
                statusLog.occurredOnISO !== occurredOnISO;
            if (hasChanged) {
                updates.push({
                    containerLogId: statusLog.id,
                    logType: ECSLLogType.STATUS,
                    occurredOnISO,
                    comments: "Container set to empty during log entry update",
                    newContainerStatus: ECSLContainerStatus.EMPTY,
                });
            }
        } else {
            // Create new status log
            creates.push({
                containerId: state.selectedContainer?.id,
                logType: ECSLLogType.STATUS,
                occurredOnISO,
                comments: "Container set to empty during log entry update",
                newContainerStatus: ECSLContainerStatus.EMPTY,
            });
        }
    } else if (statusLog) {
        // Cancel status log if not setting container as empty
        cancels.push(statusLog.id);
    }
}

async function processCancellations(containerLogIds: number[]) {
    // Process each cancellation as a separate request
    await Promise.all(
        containerLogIds.map(async (logId) => {
            const cancelRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/cancel-pending`;
            await axios.post(cancelRoute, { containerLogId: logId }, { withCredentials: true });
        }),
    );
}

async function processUpdates(updates: any[], state: IContainerLogDrawerState) {
    if (updates.length === 0) return null;
    const batchUpdateRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/batch-update`;
    const response = await axios.put<csl.IContainerLogFullDetails[]>(
        batchUpdateRoute,
        {
            containerLogs: updates,
            primarySignerId: state.primarySignee?.id,
            witnessSignerId: state.witnessSignee?.id,
        },
        { withCredentials: true },
    );
    return response.data;
}

async function processCreates(
    creates: any[],
    primarySignerId?: number,
    witnessSignerId?: number,
    batchId?: number,
) {
    if (creates.length === 0) return null;
    const createRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/batch`;
    const response = await axios.post<csl.IContainerLogFullDetails[]>(
        createRoute,
        {
            containerLogs: creates,
            primarySignerId,
            witnessSignerId,
            batchId, // If provided, associate with existing batch
        },
        { withCredentials: true },
    );
    // Extract the signature requests from the created logs
    const createdLogs = response.data || [];
    const signatureRequests = createdLogs.flatMap((log) => log.signatureRequests || []);
    return { signatureRequests };
}

const checkinContainer = createAsyncThunk<void, { containerId: number }, { rejectValue: string }>(
    "containerLogDrawer/checkinContainer",
    async (params, { rejectWithValue }) => {
        try {
            const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container/checkin`;
            await axios.post(route, { containerId: params.containerId }, { withCredentials: true });
            return;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to check in container");
        }
    },
);

export const setMedication = createAsyncThunk<
    csl.INdcVariantFullDetails | undefined,
    csl.INdcVariantFullDetails | undefined,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/setMedication",
    async (medication, { dispatch, getState, rejectWithValue }) => {
        const state = getState().containerLogDrawer;
        // If there's a container checked out, check it in first
        if (state.selectedContainer?.id) {
            try {
                await dispatch(
                    checkinContainer({ containerId: state.selectedContainer.id }),
                ).unwrap();
                return medication;
            } catch (error) {
                return rejectWithValue("Failed to check in previous container");
            }
        }
        return medication;
    },
);

export const submitSignatureRequestTask = createSubmitSignatureRequestThunk(
    "containerLogDrawer/submitSignatureRequestTask",
    "containerLogDrawer",
    [ESearchTableType.CSL_CURRENT_INVENTORY, ESearchTableType.CSL_CONTAINER_LOGS],
);

export const nullifyContainerLog = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/nullifyContainerLog",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const containerLogDrawerState = state.containerLogDrawer;
        const currentUser = state.user?.user; // Get the current user from Redux store
        if (!containerLogDrawerState.containerLogId) {
            return rejectWithValue("Missing required fields for nullification");
        }
        try {
            const nullificationPayload = {
                containerLogId: containerLogDrawerState.containerLogId,
                primarySignerId: containerLogDrawerState.primarySignee?.id,
                witnessSignerId: containerLogDrawerState.witnessSignee?.id,
            };
            // Submit nullification request
            const nullifyRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/nullify`;
            const response = await axios.post<csl.INullifyContainerLogResponse>(
                nullifyRoute,
                nullificationPayload,
                { withCredentials: true },
            );
            const signatureRequests = response.data.signatureRequests.filter(
                (sr) => sr.signer?.id === currentUser?.id,
            );
            const signaturePayloads = createSignaturePayloads(
                signatureRequests,
                containerLogDrawerState,
            );
            await submitBatchSignatures(signaturePayloads, {
                successMessage: "Log entry nullified successfully",
                showSuccessNotification: false, // We'll show our own notification
            });
            notify({
                level: "success",
                title: "Success",
                message: "Log entry nullified successfully",
            });
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
            return;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to nullify container log");
        }
    },
    createDrawerThunkMeta("hide"),
);

export const cancelNullification = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/cancelNullification",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const containerLogDrawerState = state.containerLogDrawer;
        if (!containerLogDrawerState.containerLogId) {
            return rejectWithValue("Missing container log ID");
        }
        try {
            const cancelPayload = {
                containerLogId: containerLogDrawerState.containerLogId,
            };
            const cancelRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/cancel-nullification`;
            await axios.post(cancelRoute, cancelPayload, { withCredentials: true });
            notify({
                level: "success",
                title: "Success",
                message: "Nullification process cancelled",
            });
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
            return;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to cancel nullification");
        }
    },
    createDrawerThunkMeta("hide"),
);

export const cancelPendingContainerLog = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>(
    "containerLogDrawer/cancelPendingContainerLog",
    async (_, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const containerLogDrawerState = state.containerLogDrawer;
        if (!containerLogDrawerState.containerLogId) {
            return rejectWithValue("Missing container log ID");
        }
        try {
            const cancelPayload = {
                containerLogId: containerLogDrawerState.containerLogId,
            };
            const cancelRoute = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/container-log/cancel-pending`;
            await axios.post(cancelRoute, cancelPayload, { withCredentials: true });
            notify({
                level: "success",
                title: "Success",
                message: "Pending log entry cancelled successfully",
            });
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
            return;
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to cancel pending log entry");
        }
    },
    createDrawerThunkMeta("hide"),
);

export const checkinAndCloseContainerLogDrawer = createAsyncThunk<void, void, { state: RootState }>(
    "containerLogDrawer/checkinAndCloseContainerLogDrawer",
    async (_, { dispatch, getState }) => {
        const state = getState().containerLogDrawer;
        // Check in container if one is checked out
        if (state.selectedContainer?.id) {
            try {
                await dispatch(checkinContainer({ containerId: state.selectedContainer.id }));
            } catch (error) {
                // NOTE: don't notify the user here, as the drawer is closing
                handleError(new Error("Failed to check in container"));
            }
        }
    },
    createDrawerThunkMeta("hide"),
);

export const switchToContainerLogTab = createAsyncThunk<
    ECSLContainerLogTab,
    ECSLContainerLogTab,
    { state: RootState }
>("containerLogDrawer/switchToContainerLogTab", async (newTab, { dispatch, getState }) => {
    const state = getState().containerLogDrawer;
    // If switching from USAGE tab to another tab and a container is checked out
    const switchingFromUsage =
        state.activeTab === ECSLContainerLogTab.USAGE && newTab !== ECSLContainerLogTab.USAGE;
    if (switchingFromUsage && state.selectedContainer?.id) {
        try {
            // Check in the container before switching tabs
            await dispatch(checkinContainer({ containerId: state.selectedContainer.id })).unwrap();
        } catch (error) {
            // Log error but continue with tab switch
            handleError(new Error("Failed to check in container when switching tabs"));
        }
    }
    return newTab;
});

/**
 * Refreshes container log drawer based on its current state.
 * ! NOTE: For use by drawer middleware only - handles fetching updated data
 * when a drawer is revealed after being hidden behind another drawer.
 */
export const refreshContainerLogDrawerState = createAsyncThunk<void, void, { state: RootState }>(
    "containerLogDrawer/refreshState",
    async (_, { getState, dispatch }) => {
        const state = getState().containerLogDrawer;
        // If drawer is not shown or submitting, don't refresh
        if (!state.show || state.isSubmitting) {
            return;
        }
        try {
            await match(state)
                // Case 1: Status change mode with a container selected
                .with(
                    {
                        isStatusChangeMode: true,
                        selectedContainer: P.when((c) => !!c?.id),
                    },
                    async (state_) => {
                        if (state_.selectedContainer)
                            await dispatch(
                                fetchContainerDetailsAndSet(state_.selectedContainer.id),
                            ).unwrap();
                    },
                )
                // Case 2: Disposal mode with a container selected
                .with(
                    {
                        activeTab: ECSLContainerLogTab.DISPOSAL,
                        selectedContainer: P.when((c) => !!c?.id),
                    },
                    async (state_) => {
                        // TODO this needs a sub-case for signature mode
                        if (state_.selectedContainer)
                            await dispatch(
                                fetchContainerDetailsAndSet(state_.selectedContainer.id),
                            ).unwrap();
                    },
                )
                // Case 3: Nullification mode with a container log selected
                .with(
                    {
                        isNullificationMode: true,
                        containerLogId: P.not(P.nullish),
                    },
                    async (state_) => {
                        // TODO this needs a sub-case for signature mode
                        await dispatch(fetchContainerLogDetails(state_.containerLogId)).unwrap();
                    },
                )
                // Case 4: In signature mode with a signature request
                .with(
                    {
                        isSignatureMode: true,
                        selectedContainer: P.when((c) => !!c?.id),
                    },
                    async (state_) => {
                        if (state_.selectedContainer)
                            await dispatch(
                                fetchContainerDetailsAndSet(state_.selectedContainer.id),
                            ).unwrap();
                    },
                )
                // Case 5: Selected container in regular view (not editing/creating)
                .with({ isEditMode: true }, async () => {
                    // TODO what should we fetch for this case?
                })
                // Case 6: Viewing a specific log entry (including nullification)
                .with({ containerLogId: P.not(P.nullish) }, async (state_) => {
                    await dispatch(
                        openContainerLogDrawer({
                            containerLogId: state_.containerLogId,
                        }),
                    ).unwrap();
                })
                .otherwise(async () => {
                    // No matching case found - this is a catch-all
                });
        } catch (error) {
            handleError(new Error("Failed to refresh container log drawer state"));
        }
    },
);

const containerLogDrawerSlice = createDrawerSlice({
    name: "containerLogDrawer",
    initialState: initialStateContainerLogDrawer,
    reducers: {
        hideContainerLogDrawer: {
            prepare: () => ({
                payload: undefined,
                meta: createDrawerActionMeta("hide"),
            }),
            reducer: (state) => {
                state.show = false;
            },
        },
        createNewLogEntry: {
            prepare: (params?: {
                isStatusChange?: boolean;
                targetStatus?: ECSLContainerStatus;
                container?: csl.IContainerFullDetails;
                comments?: string;
            }) => {
                return {
                    payload: {
                        drawerHistoryInstanceId: `containerLogDrawer-${generateUUID()}`,
                        ...params,
                    },
                    meta: createDrawerActionMeta("show"),
                };
            },
            reducer: (
                state,
                action: PayloadAction<{
                    drawerHistoryInstanceId: string;
                    isStatusChange?: boolean;
                    targetStatus?: ECSLContainerStatus;
                    container?: csl.IContainerFullDetails;
                    comments?: string;
                }>,
            ) => {
                const newState = {
                    ...initialStateContainerLogDrawer,
                    drawerHistoryInstanceId: action.payload.drawerHistoryInstanceId,
                    show: true,
                    occurredOn: dayjs().format(),
                };

                // If this is a status change, set up the status tab
                if (action.payload.isStatusChange) {
                    newState.activeTab = ECSLContainerLogTab.STATUS;
                    newState.isStatusChangeMode = true;
                    newState.targetContainerStatus = action.payload.targetStatus;
                    newState.containerStatusComments = action.payload.comments || "";
                    newState.selectedContainer = action.payload.container;
                } else {
                    newState.isAdministered = true;
                }

                return newState;
            },
        },
        clearCheckoutStatus: (state) => {
            state.checkoutStatus = undefined;
            state.checkoutMessage = undefined;
            state.checkoutError = null;
        },
        setPatient: (state, action: PayloadAction<patients.IAccountPatientSearch | undefined>) => {
            state.selectedPatient = action.payload;
            // Clear appointment if patient is deselected
            if (!action.payload) {
                state.selectedAppointment = undefined;
            }
        },
        setAppointment: (state, action: PayloadAction<IAppointmentLite | undefined>) => {
            state.selectedAppointment = action.payload;
        },
        setContainer: (state, action: PayloadAction<csl.IContainerFullDetails>) => {
            state.selectedContainer = action.payload;
        },
        setOccurredOn: (state, action: PayloadAction<string | undefined>) => {
            state.occurredOn = action.payload;
        },
        setAmountRemoved: (state, action: PayloadAction<number>) => {
            state.amountRemoved = action.payload;
        },
        setIsAdministered: (state, action: PayloadAction<boolean>) => {
            state.isAdministered = action.payload;
            state.primaryLogOption = undefined;
            state.secondaryLogOption = undefined;
        },
        setPrimaryLogOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.primaryLogOption = action.payload;
        },
        setSecondaryLogOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.secondaryLogOption = action.payload;
        },

        setComments: (state, action: PayloadAction<string>) => {
            state.comments = action.payload;
        },
        setPrimarySignee: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.primarySignee = action.payload;
            state.primarySignedDateTimeISO = undefined;
            state.primarySignatureBlob = undefined;
        },
        setSignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.primarySignatureBlob = action.payload;
            state.primarySignedDateTimeISO = dayjs().toISOString();
        },
        setShowWastingCard: (state, action: PayloadAction<boolean>) => {
            state.showWastingCard = action.payload;
        },
        setWastingAmount: (state, action: PayloadAction<number>) => {
            state.wastingAmount = action.payload;
        },
        setWastingMethodOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.wastingMethodOption = action.payload;
        },
        setWastingReasonOption: (state, action: PayloadAction<csl.ILogEntryOption | undefined>) => {
            state.wastingReasonOption = action.payload;
        },
        setWastingComments: (state, action: PayloadAction<string>) => {
            state.wastingComments = action.payload;
        },
        setWastingWitnessSignee: (
            state,
            action: PayloadAction<csl.ISignatureRequestSigner | undefined>,
        ) => {
            state.witnessSignee = action.payload;
            state.witnessSignedDateTimeISO = undefined;
            state.witnessSignatureBlob = undefined;
        },
        setWastingWitnessSignatureBlob: (state, action: PayloadAction<string | undefined>) => {
            state.witnessSignatureBlob = action.payload;
            state.witnessSignedDateTimeISO = dayjs().toISOString();
        },
        setShowExistingContainerModal: (state, action: PayloadAction<boolean>) => {
            state.showExistingContainerModal = action.payload;
        },
        enterNullificationMode: (state) => {
            state.isNullificationMode = true;
        },
        exitNullificationMode: (state) => {
            state.isNullificationMode = false;
            state.nullificationError = null;
        },
        enterEditMode: (state) => {
            if (!state.logDetails) {
                return;
            }

            // Only allow editing for pending logs with no completed signatures
            const isPending = state.logDetails.status === ECSLContainerLogStatus.PENDING;
            const hasNoCompletedSignatures = state.logDetails.signatureRequests.every(
                (sr) => !sr.completedOnISO,
            );

            if (!isPending || !hasNoCompletedSignatures) {
                return;
            }

            // Set edit mode and populate form with existing data
            state.isEditMode = true;

            // Copy data from logDetails to form fields
            const log = state.logDetails;
            state.selectedPatient = log.accountPatient;
            state.selectedAppointment = log.appointment;
            state.selectedContainer = log.container;
            state.selectedMedication = {
                ...log.ndcVariant,
                medication: log.medication,
            };
            state.occurredOn = log.occurredOnISO;

            // Handle log type specific data
            if (
                log.logType === ECSLLogType.ADMINISTERING ||
                log.logType === ECSLLogType.DISPENSING
            ) {
                state.isAdministered = log.logType === ECSLLogType.ADMINISTERING;
                state.amountRemoved = Math.abs(log.amount ?? 0);
                state.primaryLogOption = log.primaryLogOption;
                state.secondaryLogOption = log.secondaryLogOption;
                state.comments = log.comments;
                state.showWastingCard = false;
            } else if (log.logType === ECSLLogType.WASTING) {
                // For wasting logs, we need to show only the wasting card
                state.showWastingCard = true;
                state.wastingAmount = Math.abs(log.amount ?? 0);
                state.wastingReasonOption = log.primaryLogOption;
                state.wastingMethodOption = log.secondaryLogOption;
                state.wastingComments = log.comments;

                // Reset administering/dispensing fields since they won't be shown or used
                state.amountRemoved = undefined;
                state.isAdministered = undefined;
                state.primaryLogOption = undefined;
                state.secondaryLogOption = undefined;
                state.comments = "";
            }

            // Handle signatures
            const primarySignature = log.signatureRequests.find(
                (sr) => sr.signatureType === ECSLSignatureRequestType.PRIMARY,
            );
            if (primarySignature) {
                state.primarySignee = primarySignature.signer;
            }

            const witnessSignature = log.signatureRequests.find(
                (sr) => sr.signatureType === ECSLSignatureRequestType.WITNESS,
            );
            if (witnessSignature) {
                state.witnessSignee = witnessSignature.signer;
            }
        },
        setShowContainerSelectModal: (state, action: PayloadAction<boolean>) => {
            state.showContainerSelectModal = action.payload;
        },
        setHideAdministeringCard: (state, action: PayloadAction<boolean>) => {
            state.hideAdministeringCard = action.payload;
        },
        setStatusChangeMode: (state, action: PayloadAction<boolean>) => {
            state.isStatusChangeMode = action.payload;
            if (!action.payload) {
                state.targetContainerStatus = undefined;
                state.containerStatusComments = "";
            }
        },
        setTargetContainerStatus: (
            state,
            action: PayloadAction<ECSLContainerStatus | undefined>,
        ) => {
            state.targetContainerStatus = action.payload;
        },
        setContainerStatusComments: (state, action: PayloadAction<string>) => {
            state.containerStatusComments = action.payload;
        },
        cancelContainerStatusChange: {
            prepare: () => ({
                payload: undefined,
                meta: createDrawerActionMeta("hide"),
            }),
            reducer: (state) => {
                state.show = false;
                state.isStatusChangeMode = false;
                state.targetContainerStatus = undefined;
                state.containerStatusComments = "";
            },
        },
        setContainerAsEmpty: (state, action: PayloadAction<boolean>) => {
            state.setContainerAsEmpty = action.payload;
        },
        setSelectedMedication: (
            state,
            action: PayloadAction<csl.INdcVariantFullDetails | undefined>,
        ) => {
            state.selectedMedication = action.payload;
            // Clear container if medication is deselected
            if (!action.payload) {
                state.selectedContainer = undefined;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(openContainerLogDrawer.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.drawerHistoryInstanceId = `containerLogDrawer-${action.meta.arg.containerLogId}`;
            })
            .addCase(openContainerLogDrawer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.logDetails = action.payload;
                state.containerLogId = action.payload.id;

                // Check if log entry is nullified and set up nullification view mode
                if (action.payload.nullifiedOn) {
                    // For nullified logs, we need the nullification signature requests
                    const nullificationSignatureRequests = action.payload.signatureRequests.filter(
                        (sr) => sr.forNullification,
                    );
                    processSignatureData(state, nullificationSignatureRequests, {
                        isNullificationMode: true,
                    });
                    // Set additional view mode flag since it's not part of the standard signature options
                    state.isNullificationViewMode = true;
                }
            })
            .addCase(openContainerLogDrawer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load log entry details";
            })
            // start a container log entry by checking out a specific container
            .addCase(checkoutSpecificContainer.pending, (state, action) => {
                return {
                    ...initialStateContainerLogDrawer,
                    drawerHistoryInstanceId: `containerLogDrawer-${generateUUID()}`,
                    isSubmittingCheckOut: true,
                    checkoutError: null,
                    requestedContainerId: action.meta.arg.containerId,
                    isAdministered: true,
                };
            })
            .addCase(checkoutSpecificContainer.fulfilled, (state, action) => {
                state.isSubmittingCheckOut = false;
                state.checkoutStatus = action.payload.checkoutStatus;
                state.checkoutMessage = action.payload.checkoutMessage;
                // If checkout is successful, already checked out to self, or successfully taken over from another user
                if (
                    action.payload.checkoutStatus === ECSLCheckoutStatus.SUCCESS ||
                    action.payload.checkoutStatus === ECSLCheckoutStatus.TAKEOVER_FROM_OTHER ||
                    (action.payload.checkoutStatus ===
                        ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF &&
                        action.payload.id === state.requestedContainerId)
                ) {
                    // If we successfully took over from another staff member, show a success notification
                    if (action.payload.checkoutStatus === ECSLCheckoutStatus.TAKEOVER_FROM_OTHER) {
                        notify({
                            level: "success",
                            title: "Container Transferred",
                            message: "Successfully transferred container",
                            duration: 5000,
                        });
                    }
                    // Check if container is expired and show warning notification
                    if (dayjs(action.payload.expirationDate).isBefore(dayjs(), "day")) {
                        notify({
                            level: "warning",
                            title: "Expired Container",
                            message:
                                "This container is expired. Please consider using a non-expired container.",
                            duration: 8000,
                        });
                    }
                    state.selectedContainer = { ...action.payload };
                    state.selectedMedication = {
                        ...action.payload.ndcVariant,
                        medication: action.payload.medication,
                    };
                    state.show = true;
                    state.showExistingContainerModal = false;
                    state.requestedContainerId = undefined;
                } else {
                    handleError(
                        new Error(
                            "Unexpected checkout status when trying to start log entry for a specific container",
                        ),
                    );
                }
            })
            .addCase(checkoutSpecificContainer.rejected, (state, action) => {
                state.isSubmittingCheckOut = false;

                if (action.payload) {
                    if (
                        action.payload.status === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF &&
                        action.payload.checkout
                    ) {
                        // Handle case where user already has a different container checked out
                        state.showExistingContainerModal = true;
                        state.existingContainerCheckout = action.payload.checkout;
                        state.show = false;
                        state.checkoutError = null;
                    } else if (
                        action.payload.status === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_OTHER &&
                        action.payload.checkout
                    ) {
                        // Handle case where container is checked out by another user
                        state.showExistingContainerModal = true;
                        state.existingContainerCheckout = action.payload.checkout;
                        state.show = false;
                        state.checkoutError = null;
                    } else if (action.payload.status === ECSLCheckoutStatus.NO_TAKEOVER_ALLOWED) {
                        // Show notification and close drawer
                        notify({
                            level: "warning",
                            title: "Unable to Takeover Container",
                            message: `This container is checked out to another staff member and takeover is not allowed. To enable takeover, please ask administrator to enable takeover in controlled substance logging settings.`,
                            duration: 10000,
                        });
                        state.show = false;
                        state.showExistingContainerModal = false;
                        state.checkoutError = null;
                    } else {
                        // Handle normal error case
                        state.checkoutError = "Failed to checkout container";
                    }
                } else {
                    state.checkoutError = "Failed to checkout container";
                }
            })
            // Checkout container
            .addCase(checkoutContainer.pending, (state) => {
                state.isSubmittingCheckOut = true;
                state.checkoutError = null;
            })
            .addCase(checkoutContainer.fulfilled, (state, action) => {
                state.isSubmittingCheckOut = false;
                state.checkoutStatus = action.payload.checkoutStatus;
                state.checkoutMessage = action.payload.checkoutMessage;
                if (
                    action.payload.checkoutStatus === ECSLCheckoutStatus.SUCCESS ||
                    action.payload.checkoutStatus === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF
                ) {
                    state.selectedContainer = { ...action.payload };
                }
            })
            .addCase(checkoutContainer.rejected, (state, action) => {
                state.isSubmittingCheckOut = false;
                state.checkoutError = action.payload || "Failed to checkout container";
            })
            // Create log entry
            .addCase(createLogEntries.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(createLogEntries.fulfilled, (state, action) => {
                // Preserve ID to allow middleware to find and remove the drawer
                return {
                    ...initialStateContainerLogDrawer,
                    drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                };
            })
            .addCase(createLogEntries.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload as string;
                notify({ level: "error", title: "Error", message: action.payload as string });
            })
            // Start signature request Signing Process
            .addCase(startContainerLogSignature.pending, (state, action) => {
                return {
                    ...initialStateContainerLogDrawer,
                    isLoading: true,
                    error: null,
                    show: true,
                    drawerHistoryInstanceId: `containerLogDrawer-signature-request-id-${action.meta.arg}`,
                };
            })
            .addCase(startContainerLogSignature.fulfilled, (state, action) => {
                state.isLoading = false;
                // Process signature request data (primary signature and witness if available)
                processSignatureData(state, action.payload);
                // Find the different types of logs
                const adminLog = action.payload.containerLogs.find(
                    (log) =>
                        log.logType === ECSLLogType.ADMINISTERING ||
                        log.logType === ECSLLogType.DISPENSING,
                );
                const wastingLog = action.payload.containerLogs.find(
                    (log) => log.logType === ECSLLogType.WASTING,
                );
                const statusLog = action.payload.containerLogs.find(
                    (log) => log.logType === ECSLLogType.STATUS,
                );
                const disposalLog = action.payload.containerLogs.find(
                    (log) => log.logType === ECSLLogType.DISPOSING,
                );
                const mainLog = adminLog || wastingLog || statusLog || disposalLog;
                state.hideAdministeringCard = !adminLog;
                if (!mainLog) {
                    notify({
                        level: "error",
                        title: "Error",
                        message: "No log entry found for signature request",
                    });
                    handleError(
                        new Error(
                            "No valid log entries found for signature request when starting signing task",
                        ),
                    );
                    return;
                }
                // Check if there's a status change to Empty paired with an admin/wasting log, This indicates the "Set Container as Empty" toggle was checked during creation
                if (
                    statusLog &&
                    statusLog.newContainerStatus === ECSLContainerStatus.EMPTY &&
                    (adminLog || wastingLog)
                ) {
                    state.setContainerAsEmpty = true;
                } else {
                    state.setContainerAsEmpty = false;
                }
                // Process based on log type
                if (mainLog.logType === ECSLLogType.STATUS) {
                    // Status change log
                    state.isStatusChangeMode = true;
                    state.activeTab = ECSLContainerLogTab.STATUS;
                    state.containerLogId = mainLog.id;
                    state.logDetails = mainLog;
                    state.selectedContainer = mainLog.container;
                    state.targetContainerStatus = mainLog.newContainerStatus;
                    state.containerStatusComments = mainLog.comments;
                    state.occurredOn = mainLog.occurredOnISO;
                } else if (mainLog.logType === ECSLLogType.DISPOSING) {
                    // Disposal log
                    state.isStatusChangeMode = false;
                    state.activeTab = ECSLContainerLogTab.DISPOSAL;
                    state.selectedContainer = mainLog.container;
                    state.selectedMedication = mainLog.ndcVariant;
                    state.occurredOn = mainLog.occurredOnISO;
                    state.primaryLogOption = mainLog.primaryLogOption; // Disposal method
                    state.secondaryLogOption = mainLog.secondaryLogOption; // Destruction method
                    state.comments = mainLog.comments; // Disposal comments
                } else {
                    // Admin/dispensing or wasting log
                    state.isStatusChangeMode = false;
                    state.activeTab = ECSLContainerLogTab.USAGE;

                    // Common data for all usage logs
                    state.selectedPatient = mainLog.accountPatient;
                    state.selectedAppointment = mainLog.appointment;
                    state.selectedContainer = mainLog.container;
                    state.selectedMedication = mainLog.ndcVariant;
                    state.occurredOn = mainLog.occurredOnISO;
                    state.amountRemoved = Math.abs(mainLog.amount ?? 0);

                    // Admin/dispensing specific data
                    if (adminLog) {
                        state.isAdministered = adminLog.logType === ECSLLogType.ADMINISTERING;
                        state.primaryLogOption = adminLog.primaryLogOption;
                        state.secondaryLogOption = adminLog.secondaryLogOption;
                        state.comments = adminLog.comments;
                    } else {
                        state.isAdministered = undefined;
                    }
                    // Wasting specific data
                    if (wastingLog) {
                        state.showWastingCard = true;
                        state.wastingAmount = Math.abs(wastingLog.amount ?? 0);
                        state.wastingReasonOption = wastingLog.primaryLogOption;
                        state.wastingMethodOption = wastingLog.secondaryLogOption;
                        state.wastingComments = wastingLog.comments;
                    }
                }
            })
            .addCase(startContainerLogSignature.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load signature request details";
            })
            // Submit signature request task
            .addCase(submitSignatureRequestTask.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitSignatureRequestTask.fulfilled, (state) => {
                return { ...state, show: false };
            })
            .addCase(submitSignatureRequestTask.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to submit signatures";
            })
            // Initialize log entry with appointment
            .addCase(initializeLogEntryWithAppointment.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.drawerHistoryInstanceId = `containerLogDrawer-${generateUUID()}`;
            })
            .addCase(initializeLogEntryWithAppointment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedAppointment = action.payload.appointment;
                state.selectedPatient = action.payload.patient;
            })
            .addCase(initializeLogEntryWithAppointment.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load appointment details";
                state.show = false;
            })
            // Create log option
            .addCase(createLogOption.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(createLogOption.fulfilled, (state, action) => {
                const newState = { ...state, isSubmitting: false };
                const option = action.payload;
                switch (option.enumType) {
                    // Primary options
                    case ECSLLogOptionType.ADMINISTRATION_REASON:
                        if (newState.isAdministered) {
                            newState.primaryLogOption = option;
                        }
                        break;
                    case ECSLLogOptionType.DISPENSING_REASON:
                        if (!newState.isAdministered) {
                            newState.primaryLogOption = option;
                        }
                        break;
                    case ECSLLogOptionType.WASTING_REASON:
                        newState.wastingReasonOption = option;
                        break;
                    case ECSLLogOptionType.DISPOSAL_METHOD:
                        newState.primaryLogOption = option;
                        break;
                    // Secondary options
                    case ECSLLogOptionType.INJECTION_SITE:
                        if (newState.isAdministered) {
                            newState.secondaryLogOption = option;
                        }
                        break;
                    case ECSLLogOptionType.DESTRUCTION_METHOD:
                        newState.wastingMethodOption = option;
                        break;
                }
                return newState;
            })
            .addCase(createLogOption.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to create log option";
            })
            .addCase(startContainerLogNullificationSigning.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.drawerHistoryInstanceId = `containerLogDrawer-nullification-signature-request-id-${action.meta.arg}`;
            })
            .addCase(startContainerLogNullificationSigning.fulfilled, (state, action) => {
                state.isLoading = false;
                // Get the main container log from the response
                const mainLog = action.payload.containerLogs[0];
                if (!mainLog) {
                    notify({
                        level: "error",
                        title: "Error",
                        message: "No log entry found for nullification signature request",
                    });
                    return;
                }
                const signatureRequests = action.payload.containerLogs.flatMap(
                    (log) => log.signatureRequests,
                );
                // Process nullification signature data with all signatures
                processSignatureData(state, signatureRequests, {
                    isNullificationMode: true,
                    mainSignatureRequest: action.payload.signatureRequest,
                });
                // Set container log specific details
                state.containerLogId = mainLog.id;
                state.logDetails = mainLog;
                // Set usage-specific state
                state.isAdministered =
                    mainLog.logType === ECSLLogType.ADMINISTERING ||
                    mainLog.logType === ECSLLogType.DISPENSING
                        ? mainLog.logType === ECSLLogType.ADMINISTERING
                        : undefined;
                // Set entity references
                state.selectedPatient = mainLog.accountPatient;
                state.selectedAppointment = mainLog.appointment;
                state.selectedContainer = mainLog.container;
                state.selectedMedication = mainLog.ndcVariant;
            })
            .addCase(startContainerLogNullificationSigning.rejected, (state, action) => {
                state.isLoading = false;
                state.error =
                    action.payload || "Failed to load nullification signature request details";
            })
            // Existing cases for nullifyContainerLog
            .addCase(nullifyContainerLog.pending, (state) => {
                state.isSubmitting = true;
                state.nullificationError = null;
            })
            .addCase(nullifyContainerLog.fulfilled, (state) => {
                state.isSubmitting = false;
                state.show = false; // Close drawer on success
            })
            .addCase(nullifyContainerLog.rejected, (state, action) => {
                state.isSubmitting = false;
                state.nullificationError = action.payload as string;
            })
            .addCase(cancelNullification.pending, (state) => {
                state.isSubmitting = true;
                state.nullificationError = null;
                state.show = false;
            })
            .addCase(cancelNullification.fulfilled, (state) => {
                state.isSubmitting = false;
                state.isNullificationMode = false;
                state.nullificationError = null;
                // Reset nullification-related fields
                state.primarySignee = undefined;
                state.primarySignatureBlob = undefined;
                state.witnessSignee = undefined;
                state.witnessSignatureBlob = undefined;
            })
            .addCase(cancelNullification.rejected, (state, action) => {
                state.isSubmitting = false;
                state.nullificationError = action.payload as string;
            })
            // Include checkout container cases from first file
            .addCase(checkinContainer.pending, (state) => {
                state.isSubmittingCheckOut = true;
            })
            .addCase(checkinContainer.fulfilled, (state) => {
                state.isSubmittingCheckOut = false;
            })
            .addCase(checkinContainer.rejected, (state) => {
                state.isSubmittingCheckOut = false;
            })
            .addCase(setMedication.pending, (state) => {
                state.isSubmittingCheckOut = true;
            })
            .addCase(setMedication.fulfilled, (state, action) => {
                state.selectedMedication = action.payload;
                state.isSubmittingCheckOut = false;
                // Clear container-related state
                state.selectedContainer = undefined;
                state.checkoutStatus = undefined;
                state.checkoutMessage = undefined;
                // forcing them to re-sign because they are changing the medication
                state.primarySignatureBlob = undefined;
                state.primarySignedDateTimeISO = undefined;
                state.witnessSignatureBlob = undefined;
                state.witnessSignedDateTimeISO = undefined;
            })
            .addCase(setMedication.rejected, (state, action) => {
                state.isSubmittingCheckOut = false;
            })
            // Include common cases
            .addCase(cancelPendingContainerLog.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(cancelPendingContainerLog.fulfilled, (state) => {
                state.isSubmitting = false;
                state.show = false; // Close drawer on success
            })
            .addCase(cancelPendingContainerLog.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload as string;
            })
            .addCase(updateLogEntries.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(updateLogEntries.fulfilled, (state) => {
                state.isSubmitting = false;
                state.show = false; // Close drawer on success
            })
            .addCase(updateLogEntries.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to update log entry";
            })
            // Include override container cases from second file
            .addCase(overrideSelectedContainer.pending, (state, action) => {
                state.requestedContainerId = action.meta.arg.containerId;
                state.isSubmittingCheckOut = true;
                state.checkoutError = null;
            })
            .addCase(overrideSelectedContainer.fulfilled, (state, action) => {
                state.isSubmittingCheckOut = false;
                if (!action.payload) {
                    state.checkoutError = "Failed to checkout container";
                    return;
                }
                state.checkoutStatus = action.payload.checkoutStatus;
                state.checkoutMessage = action.payload.checkoutMessage;
                // If checkout is successful, already checked out to self, or successfully taken over from another user
                if (
                    action.payload.checkoutStatus === ECSLCheckoutStatus.SUCCESS ||
                    action.payload.checkoutStatus === ECSLCheckoutStatus.TAKEOVER_FROM_OTHER ||
                    (action.payload.checkoutStatus ===
                        ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_SELF &&
                        action.payload.id === state.requestedContainerId)
                ) {
                    // If we successfully took over from another staff member, show a success notification
                    if (action.payload.checkoutStatus === ECSLCheckoutStatus.TAKEOVER_FROM_OTHER) {
                        notify({
                            level: "success",
                            title: "Container Transferred",
                            message: "Successfully transferred container",
                            duration: 5000,
                        });
                    }
                    // Check if container is expired and show warning notification
                    if (dayjs(action.payload.expirationDate).isBefore(dayjs(), "day")) {
                        notify({
                            level: "warning",
                            title: "Expired Container",
                            message:
                                "This container is expired. Please consider using a non-expired container.",
                            duration: 8000,
                        });
                    }
                    state.selectedContainer = { ...action.payload };
                    state.selectedMedication = {
                        ...action.payload.ndcVariant,
                        medication: action.payload.medication,
                    };
                    state.showExistingContainerModal = false;
                    state.requestedContainerId = undefined;
                } else {
                    handleError(
                        new Error(
                            "Unexpected checkout status when trying to start log entry for a specific container",
                        ),
                    );
                }
            })
            .addCase(overrideSelectedContainer.rejected, (state, action) => {
                state.isSubmittingCheckOut = false;
                if (action.payload) {
                    if (action.payload.status === ECSLCheckoutStatus.CONTAINER_PENDING) {
                        // Handle pending container error
                        notify({
                            level: "warning",
                            title: "Container Not Available",
                            message:
                                "This container is pending signature approval and cannot be used yet.",
                            duration: 8000,
                        });
                        state.showExistingContainerModal = false;
                        state.checkoutError = null;
                    } else if (
                        action.payload.status === ECSLCheckoutStatus.ALREADY_CHECKED_OUT_TO_OTHER &&
                        action.payload.checkout
                    ) {
                        // Handle case where container is checked out by another user
                        state.showExistingContainerModal = true;
                        state.existingContainerCheckout = action.payload.checkout;
                        state.checkoutError = null;
                    } else if (action.payload.status === ECSLCheckoutStatus.NO_TAKEOVER_ALLOWED) {
                        // Show notification and close drawer
                        notify({
                            level: "warning",
                            title: "Unable to Takeover Container",
                            message: `This container is checked out to another staff member and takeover is not allowed. To enable takeover, please ask administrator to enable takeover in controlled substance logging settings.`,
                            duration: 10000,
                        });
                        state.showExistingContainerModal = false;
                        state.checkoutError = null;
                    } else {
                        // Handle normal error case
                        state.checkoutError = "Failed to checkout container";
                    }
                } else {
                    state.checkoutError = "Failed to checkout container";
                }
            })
            .addCase(switchContainerForStatusChange.pending, (state) => {
                state.isSubmitting = true; // using isSubmitting instead of isLoading because isLoading = true will remove all drawer content
                state.error = null;
            })
            .addCase(switchContainerForStatusChange.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.selectedContainer = action.payload;
            })
            .addCase(switchContainerForStatusChange.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to select container for status change";
            })
            .addCase(fetchContainerDetailsAndSet.fulfilled, (state, action) => {
                // Only update if we have an active container and the IDs match
                if (state.selectedContainer && state.selectedContainer.id === action.payload.id) {
                    state.selectedContainer = action.payload;
                }
            })
            .addCase(switchToContainerLogTab.fulfilled, (state, action) => {
                const newTab = action.payload;
                if (state.activeTab === newTab) {
                    return state;
                }
                if (newTab === ECSLContainerLogTab.USAGE) {
                    return {
                        ...initialStateContainerLogDrawer,
                        activeTab: ECSLContainerLogTab.USAGE,
                        drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                        occurredOn: dayjs().format(),
                        show: true, // Keep the drawer open
                    };
                } else if (newTab === ECSLContainerLogTab.STATUS) {
                    return {
                        ...initialStateContainerLogDrawer,
                        activeTab: ECSLContainerLogTab.STATUS,
                        drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                        isStatusChangeMode: true,
                        occurredOn: dayjs().format(),
                        show: true, // this is preventing it from closing but this reducer should be called when the drawer is already open
                    };
                } else {
                    return {
                        ...initialStateContainerLogDrawer,
                        activeTab: ECSLContainerLogTab.DISPOSAL,
                        drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                        occurredOn: dayjs().format(),
                        show: true, // Keep the drawer open
                    };
                }
            })
            .addCase(switchToContainerLogTab.rejected, (state, action) => {
                // Tab switch should still proceed even if the check-in fails
                const newTab = action.meta.arg;
                if (state.activeTab === newTab) {
                    return state;
                }
                if (newTab === ECSLContainerLogTab.USAGE) {
                    return {
                        ...initialStateContainerLogDrawer,
                        activeTab: ECSLContainerLogTab.USAGE,
                        drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                        occurredOn: dayjs().format(),
                        show: true,
                    };
                } else if (newTab === ECSLContainerLogTab.STATUS) {
                    return {
                        ...initialStateContainerLogDrawer,
                        activeTab: ECSLContainerLogTab.STATUS,
                        drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                        isStatusChangeMode: true,
                        occurredOn: dayjs().format(),
                        show: true,
                    };
                } else {
                    return {
                        ...initialStateContainerLogDrawer,
                        activeTab: ECSLContainerLogTab.DISPOSAL,
                        drawerHistoryInstanceId: state.drawerHistoryInstanceId,
                        occurredOn: dayjs().format(),
                        show: true,
                    };
                }
            })
            .addCase(enterBatchEditMode.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
                state.show = true;
                state.drawerHistoryInstanceId = `containerLogDrawer-batch-${action.meta.arg}`;
            })
            .addCase(enterBatchEditMode.fulfilled, (state, action) => {
                state.isLoading = false;
                // Store batch details
                state.batchId = action.payload.id;
                state.batchType = action.payload.batchType;
                state.containerLogs = action.payload.containerLogs;
                if (!action.payload.containerLogs || action.payload.containerLogs.length === 0) {
                    state.error = "No related logs found";
                    return;
                }
                // Find logs by type
                const adminLog = action.payload.containerLogs.find(
                    (log) =>
                        log.logType === ECSLLogType.ADMINISTERING ||
                        log.logType === ECSLLogType.DISPENSING,
                );
                const wastingLog = action.payload.containerLogs.find(
                    (log) => log.logType === ECSLLogType.WASTING,
                );
                const statusLog = action.payload.containerLogs.find(
                    (log) => log.logType === ECSLLogType.STATUS,
                );
                // Get container info from first available log
                const mainLog =
                    adminLog || wastingLog || statusLog || action.payload.containerLogs[0];
                state.selectedContainer = mainLog.container;
                // Set appropriate containerLogId for later reference
                state.containerLogId = mainLog.id;
                // Set medication info
                if (mainLog.ndcVariant) {
                    state.selectedMedication = {
                        ...mainLog.ndcVariant,
                        medication: mainLog.medication,
                    };
                }
                // Set time - use the most recent time if multiple logs
                const sortedLogs = [...action.payload.containerLogs].sort(
                    (a, b) =>
                        new Date(b.occurredOnISO).getTime() - new Date(a.occurredOnISO).getTime(),
                );
                state.occurredOn = sortedLogs[0]?.occurredOnISO;
                // Set administering/dispensing info
                if (adminLog) {
                    state.hideAdministeringCard = false;
                    state.isAdministered = adminLog.logType === ECSLLogType.ADMINISTERING;
                    state.amountRemoved = Math.abs(adminLog.amount ?? 0);
                    state.primaryLogOption = adminLog.primaryLogOption;
                    state.secondaryLogOption = adminLog.secondaryLogOption;
                    state.selectedPatient = adminLog.accountPatient;
                    state.selectedAppointment = adminLog.appointment;
                    state.comments = adminLog.comments || "";
                } else {
                    state.hideAdministeringCard = true;
                }
                // Set wasting info
                if (wastingLog) {
                    state.showWastingCard = true;
                    state.wastingAmount = Math.abs(wastingLog.amount ?? 0);
                    state.wastingReasonOption = wastingLog.primaryLogOption;
                    state.wastingMethodOption = wastingLog.secondaryLogOption;
                    state.wastingComments = wastingLog.comments || "";
                } else {
                    state.showWastingCard = false;
                }
                // Set status change info
                if (statusLog && statusLog.newContainerStatus === ECSLContainerStatus.EMPTY) {
                    state.setContainerAsEmpty = true;
                } else {
                    state.setContainerAsEmpty = false;
                }
                // Set signers from signature requests
                if (
                    action.payload.signatureRequests &&
                    action.payload.signatureRequests.length > 0
                ) {
                    const primarySig = action.payload.signatureRequests.find(
                        (sr) => sr.signatureType === ECSLSignatureRequestType.PRIMARY,
                    );
                    const witnessSig = action.payload.signatureRequests.find(
                        (sr) => sr.signatureType === ECSLSignatureRequestType.WITNESS,
                    );
                    if (primarySig) {
                        state.primarySignee = primarySig.signer;
                    }
                    if (witnessSig) {
                        state.witnessSignee = witnessSig.signer;
                    }
                }
                // Enable edit mode
                state.isEditMode = true;
            })
            .addCase(enterBatchEditMode.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load batch details";
            });
    },
});

export const {
    createNewLogEntry,
    hideContainerLogDrawer,
    setPatient,
    setAppointment,
    setContainer,
    setAmountRemoved,
    setIsAdministered,
    setPrimaryLogOption,
    setSecondaryLogOption,
    setComments,
    setOccurredOn,
    setPrimarySignee,
    setSignatureBlob,
    setShowWastingCard,
    setWastingAmount,
    setWastingMethodOption,
    setWastingReasonOption,
    setWastingComments,
    setWastingWitnessSignee,
    setWastingWitnessSignatureBlob,
    setShowExistingContainerModal,
    enterNullificationMode,
    exitNullificationMode,
    enterEditMode,
    setShowContainerSelectModal,
    setHideAdministeringCard,
    setStatusChangeMode,
    setTargetContainerStatus,
    setContainerStatusComments,
    cancelContainerStatusChange,
    setContainerAsEmpty,
    setSelectedMedication,
} = containerLogDrawerSlice.actions;

export default containerLogDrawerSlice.reducer;
